<template>
    <div class="reports-menu">
        <div class="reports-menu__header">
            Categories
        </div>
        <ul class="reports-menu__list">
            <li 
                class="reports-menu__item"
                :class="{ 'reports-menu__item--active': currentCategory === '' }"
                @click="selectCategory('')"
            >
                <div class="reports-menu__item-content">
                    <div class="reports-menu__item-label">All Reports</div>
                    <div class="reports-menu__item-count">{{ allReports.length }}</div>
                </div>
            </li>
            
            <li 
                v-for="category in categoriesWithCounts" 
                :key="category.id"
                class="reports-menu__item"
                :class="{ 'reports-menu__item--active': currentCategory === category.id }"
            >
                <div 
                    class="reports-menu__item-content"
                    @click="toggleCategory(category.id)"
                >
                    <div class="reports-menu__item-label">{{ category.name }}</div>
                    <div class="reports-menu__item-count">{{ category.report_count }}</div>
                </div>
                
                <transition name="fade">
                    <ul v-if="categoryVisible(category.id)" class="reports-menu__subitems">
                        <li 
                            v-for="report in getReportsByCategory(category.id)" 
                            :key="report.id"
                            class="reports-menu__subitem"
                        >
                            <a :href="report.prep_url" class="reports-menu__subitem-link">
                                {{ report.name }}
                            </a>
                        </li>
                    </ul>
                </transition>
            </li>
        </ul>
    </div>
</template>

<script>
import { mapState } from 'vuex';

export default {
    name: 'ReportsMenuV1',
    
    props: {
        reportData: {
            type: Array,
            default: () => []
        },
        showCategory: {
            type: Number,
            required: false
        },
        selectedCategory: {
            type: [String, Number],
            default: ''
        }
    },

    data() {
        return {
            currentCategory: this.selectedCategory || this.showCategory || '',
            visibleCategories: {}
        };
    },

    created() {
        this.$store.dispatch('loadReports');
    },

    computed: {
        ...mapState(['reports']),

        allReports() {
            if (this.reportData && this.reportData.length) {
                return this.reportData;
            }
            return this.reports.all || [];
        },

        availableCategories() {
            return this.reports.categories || [];
        },

        categoriesWithCounts() {
            return this.availableCategories.map(category => ({
                ...category,
                report_count: this.getReportsByCategory(category.id).length
            })).filter(cat => cat.report_count > 0);
        }
    },

    mounted() {
        // Both halves used to live in separate mounted hooks. Vue keeps only the
        // last one, so seeding the store never ran and reports.categories stayed
        // empty.
        if (this.reportData && this.reportData.length) {
            this.$store.commit('updateReports', { type: 'all', items: this.reportData });
        }

        // Auto-expand the selected category if provided
        if (this.currentCategory) {
            this.$set(this.visibleCategories, this.currentCategory, true);
        }
    },

    methods: {
        selectCategory(categoryId) {
            // If clicking "All Reports", navigate to reports index
            if (categoryId === '') {
                window.location = '/reports';
                return;
            }

            // Toggle the category visibility
            const isCurrentlyVisible = this.visibleCategories[categoryId];
            this.$set(this.visibleCategories, categoryId, !isCurrentlyVisible);
            
            // Set as selected category
            this.currentCategory = categoryId;
            this.$emit('category-selected', categoryId);
        },

        toggleCategory(categoryId) {
            this.$set(this.visibleCategories, categoryId, !this.visibleCategories[categoryId]);
        },

        categoryVisible(categoryId) {
            return this.visibleCategories[categoryId] || false;
        },

        getReportsByCategory(categoryId) {
            return this.allReports.filter(report => report.category_id === categoryId);
        }
    }
};
</script>
